import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CircleDashed, Edit, History, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface TireCardProps {
  tire: any;
  onEdit: () => void;
  onHistory: () => void;
  onInstall?: () => void;
  onRemove?: () => void;
}

export function TireCard({ tire, onEdit, onHistory, onInstall, onRemove }: TireCardProps) {
  const statusConfig = {
    installed: { label: 'Instalado', color: 'bg-green-500' },
    stock: { label: 'Estoque', color: 'bg-blue-500' },
    repair: { label: 'Reparo', color: 'bg-orange-500' },
    retreading: { label: 'Recapagem', color: 'bg-purple-500' },
    discarded: { label: 'Descartado', color: 'bg-gray-500' }
  };

  const status = statusConfig[tire.status as keyof typeof statusConfig] || statusConfig.stock;

  return (
    <Card className="p-4 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <CircleDashed className="h-5 w-5 text-muted-foreground" />
          <div>
            <h3 className="font-semibold">{tire.brand} {tire.model}</h3>
            <p className="text-sm text-muted-foreground">Série: {tire.serial_number}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onHistory}>
              <History className="h-4 w-4 mr-2" />
              Histórico
            </DropdownMenuItem>
            {tire.status === 'stock' && onInstall && (
              <DropdownMenuItem onClick={onInstall}>
                Instalar
              </DropdownMenuItem>
            )}
            {tire.status === 'installed' && onRemove && (
              <DropdownMenuItem onClick={onRemove}>
                Remover
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Medida:</span>
          <span className="font-medium">{tire.size}</span>
        </div>
        
        {tire.vehicle && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Veículo:</span>
            <span className="font-medium">{tire.vehicle.plate}</span>
          </div>
        )}

        {tire.current_position && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Posição:</span>
            <span className="font-medium">{tire.current_position}</span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">KM Rodados:</span>
          <span className="font-medium">{tire.total_km_driven?.toLocaleString() || 0} km</span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Banda:</span>
          <span className="font-medium">{tire.tread_depth_mm || 0} mm</span>
        </div>

        <div className="pt-2">
          <Badge className={status.color}>{status.label}</Badge>
        </div>
      </div>
    </Card>
  );
}
